import { Link, NavLink, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useState } from "react";
import {
    LayoutDashboard,
    FileText,
    CalendarClock,
    MapPin,
    UserCircle2,
    LogOut,
    Menu,
    ChevronRight,
    ClipboardList,
    Calendar,
} from "lucide-react";
import { useAuth, LOGO_URL } from "@/lib/api";
import { toast } from "sonner";

const NAV = [
    { to: "/formateur", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/formateur/sessions", label: "Mes sessions", icon: Calendar },
    { to: "/formateur/evaluations", label: "Évaluations", icon: ClipboardList },
    { to: "/formateur/profil-public", label: "Profil public", icon: UserCircle2 },
    { to: "/formateur/programme", label: "Mon programme", icon: FileText },
    { to: "/formateur/disponibilites", label: "Disponibilités", icon: CalendarClock },
    { to: "/formateur/villes", label: "Villes d'intervention", icon: MapPin },
];

function crumbFor(pathname) {
    if (pathname === "/formateur") return "Dashboard";
    if (pathname.includes("/sessions")) return "Mes sessions";
    if (pathname.includes("/evaluations")) return "Évaluations";
    if (pathname.includes("/programme")) return "Mon programme";
    if (pathname.includes("/disponibilites")) return "Disponibilités";
    if (pathname.includes("/villes")) return "Villes";
    return "Formateur";
}

export default function FormateurLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        toast.success("Déconnecté");
        navigate("/login", { replace: true });
    };

    const Sidebar = (
        <aside className="flex h-full w-72 flex-col bg-brand-ink text-white">
            <div className="flex items-center gap-3 px-6 py-7 border-b border-white/10">
                <img src={LOGO_URL} alt="Cursus Physio" className="h-8 w-auto brightness-0 invert" />
            </div>
            <div className="px-6 py-6 text-[11px] font-semibold uppercase tracking-[0.25em] text-white/40">
                Espace formateur
            </div>
            <nav className="flex-1 space-y-1 px-4">
                {NAV.map((item) => {
                    const Icon = item.icon;
                    return (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            onClick={() => setOpen(false)}
                            data-testid={`formateur-nav-${item.label.toLowerCase().replace(/\s+/g, "-").replace(/'/g, "")}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                                    isActive
                                        ? "bg-brand-orange text-white shadow-[0_4px_16px_rgba(232,135,60,0.3)]"
                                        : "text-white/70 hover:bg-white/5 hover:text-white"
                                }`
                            }
                        >
                            <Icon className="h-4 w-4" /> {item.label}
                        </NavLink>
                    );
                })}
            </nav>
            <div className="border-t border-white/10 p-4">
                <div className="mb-3 rounded-xl bg-white/[0.03] p-3">
                    <div className="text-[10px] uppercase tracking-wider text-white/40">Connecté</div>
                    <div className="mt-1 truncate text-sm font-medium">{user?.first_name} {user?.last_name}</div>
                    <div className="mt-0.5 truncate text-xs text-white/50">{user?.email}</div>
                </div>
                <button
                    onClick={handleLogout}
                    data-testid="formateur-logout"
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                >
                    <LogOut className="h-4 w-4" /> Déconnexion
                </button>
            </div>
        </aside>
    );

    return (
        <div className="flex min-h-screen bg-[#FAFAFA]" data-testid="formateur-layout">
            <div className="hidden lg:flex">{Sidebar}</div>
            {open && (
                <div className="fixed inset-0 z-40 flex lg:hidden">
                    <div className="absolute inset-0 bg-brand-ink/50" onClick={() => setOpen(false)} />
                    <div className="relative h-full">{Sidebar}</div>
                </div>
            )}
            <div className="flex min-h-screen flex-1 flex-col">
                <header className="flex items-center gap-4 border-b border-brand-line bg-white px-6 py-4">
                    <button className="lg:hidden" onClick={() => setOpen(true)} aria-label="Menu">
                        <Menu className="h-5 w-5" />
                    </button>
                    <div className="flex items-center gap-2 text-sm text-brand-smoke">
                        <Link to="/formateur" className="hover:text-brand-ink">Espace formateur</Link>
                        <ChevronRight className="h-3 w-3" />
                        <span className="font-medium text-brand-ink">{crumbFor(location.pathname)}</span>
                    </div>
                    <div className="ml-auto flex items-center gap-3">
                        <div className="hidden text-right md:block">
                            <div className="text-sm font-medium text-brand-ink">{user?.first_name} {user?.last_name}</div>
                            <div className="text-xs text-brand-smoke">Formateur</div>
                        </div>
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange text-white font-display text-sm">
                            {(user?.first_name || "?")[0]?.toUpperCase()}
                        </div>
                    </div>
                </header>
                <main className="flex-1 overflow-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
